<script setup lang="ts">
import PanelGroup from '../PanelGroup.vue'
import { ArrowUpRight, Store, TriangleAlert } from 'lucide-vue-next'
import { BRAND_APP_STORE_ACTIONS, missingStoreActions, type BrandAppShowcaseStore } from '../../lib/brandAppContract'
import useBrandAppConfig from '../../composables/useBrandAppConfig'

defineProps<{
  stores: BrandAppShowcaseStore[]
}>()

const { t } = useI18n()
const { order, storesScreen, isStoreHidden, setStoreVisible } = useBrandAppConfig()

const missingWarning = (store: BrandAppShowcaseStore) => {
  const missing = missingStoreActions(store, storesScreen.value.actions, order.value)
  if (missing.length === 0) return null
  const actions = BRAND_APP_STORE_ACTIONS.filter((action) => missing.includes(action.id))
  const data = actions.map((action) => action.missing).join(', ')
  const labels = actions.map((action) => action.label).join(` ${t('pages.loyaltySupports.webapp.editor.stores.and')} `)

  return t('pages.loyaltySupports.webapp.editor.stores.list.missing', { data, labels }, actions.length)
}

const subtitle = (store: BrandAppShowcaseStore) => {
  if (isStoreHidden(store.id))
    return { text: t('pages.loyaltySupports.webapp.editor.stores.list.hidden'), warning: false }
  const warning = missingWarning(store)
  if (warning) return { text: warning, warning: true }

  return { text: store.city ?? store.address ?? '', warning: false }
}
</script>

<template>
  <PanelGroup>
    <div
      v-if="stores.length === 0"
      class="border-border flex flex-col items-start gap-2 rounded-md border border-dashed p-4"
    >
      <p class="text-muted-foreground text-sm">{{ t('pages.loyaltySupports.webapp.editor.stores.list.empty') }}</p>
      <Button variant="link" class="px-0" as-child>
        <RouterLink :to="{ name: 'StoreList' }">
          {{ t('pages.loyaltySupports.webapp.editor.stores.list.manage') }}
          <ArrowUpRight />
        </RouterLink>
      </Button>
    </div>

    <template v-else>
      <p class="text-muted-foreground text-xs">
        {{ t('pages.loyaltySupports.webapp.editor.stores.list.hint') }}
      </p>
      <div class="flex flex-col gap-2">
        <div
          v-for="(store, index) in stores"
          :key="store.id"
          class="motion-safe:animate-card-in flex items-center gap-3 rounded-xl border p-3 transition-colors"
          :class="[
            isStoreHidden(store.id) ? 'bg-muted/50' : 'bg-background shadow-surface',
            subtitle(store).warning ? 'border-amber-300' : 'border-border'
          ]"
          :style="{ animationDelay: `${Math.min(index, 5) * 40}ms` }"
        >
          <span
            class="flex size-10 shrink-0 items-center justify-center rounded-xl"
            :class="isStoreHidden(store.id) ? 'bg-background' : 'bg-muted'"
          >
            <Store class="size-4.5" :class="isStoreHidden(store.id) ? 'text-foreground-disabled' : 'text-primary'" />
          </span>
          <label :for="`brandApp.stores.${store.id}.visible`" class="flex min-w-0 flex-1 cursor-pointer flex-col">
            <span
              class="truncate text-sm font-medium"
              :class="isStoreHidden(store.id) ? 'text-foreground-secondary' : 'text-foreground'"
            >
              {{ store.name }}
            </span>
            <span
              v-if="subtitle(store).text"
              class="flex items-center gap-1 truncate text-xs"
              :class="subtitle(store).warning ? 'text-amber-700' : 'text-muted-foreground'"
            >
              <TriangleAlert v-if="subtitle(store).warning" class="size-3 shrink-0" />
              {{ subtitle(store).text }}
            </span>
          </label>
          <Button
            variant="ghost"
            size="icon-sm"
            as-child
            :aria-label="t('pages.loyaltySupports.webapp.editor.stores.list.openStore')"
          >
            <RouterLink :to="{ name: 'StoreEdit', params: { storeId: store.id } }">
              <ArrowUpRight />
            </RouterLink>
          </Button>
          <Switch
            :id="`brandApp.stores.${store.id}.visible`"
            :model-value="!isStoreHidden(store.id)"
            @update:model-value="setStoreVisible(store.id, $event)"
          />
        </div>
      </div>
    </template>
  </PanelGroup>
</template>
